/**
 * The file-choice gate for a Splitwise import: what we accept, and what a
 * rejection says. Pure and key-returning — core has no i18n context and no
 * `File`, so web hands it the text it read and mobile hands it the text the
 * document picker gave back.
 */
import type { SplitwiseImportRequestDto } from '@splitfiesta/shared';

import type { LabelResolutions } from './labelResolution';
import { SplitwiseParseError, parseSplitwiseCsv } from './splitwiseCsv';
import type { SplitwiseFile } from './splitwiseCsv';

export const MAX_IMPORT_FILE_BYTES = 2 * 1024 * 1024;
export const MAX_GROUP_NAME_LENGTH = 100;
/** Placeholder members are stored with a 50-char name, half the DTO's cap. */
export const MAX_MEMBER_NAME_LENGTH = 50;
/** The API refuses a body this big, and it never reaches a job to resume. */
export const MAX_IMPORT_PAYLOAD_BYTES = 4 * 1024 * 1024;
/** One placeholder member per column, and the group creation caps them here. */
export const MAX_IMPORT_MEMBERS = 50;

export interface ImportFileError {
  key: string;
  params?: Record<string, string | number>;
}

export type ImportFileResult =
  { file: SplitwiseFile } | { error: ImportFileError };

export function validateImportFile(file: {
  name: string;
  size: number;
}): ImportFileError | null {
  // Browsers and document pickers report CSV MIME types inconsistently
  // (text/csv, application/vnd.ms-excel, sometimes ""), so the extension is
  // the check that actually holds.
  if (!file.name.toLowerCase().endsWith('.csv'))
    return { key: 'import:errors.notCsv' };
  if (file.size > MAX_IMPORT_FILE_BYTES)
    return { key: 'import:errors.fileTooLarge' };
  return null;
}

function parseErrorToKey(error: SplitwiseParseError): ImportFileError {
  switch (error.code) {
    case 'not-splitwise':
      return {
        key: 'import:errors.notSplitwise',
        params: { header: (error.header ?? []).join(', ') },
      };
    case 'invalid-header':
      return { key: 'import:errors.invalidHeader' };
    case 'invalid-currency':
      return {
        key: 'import:errors.invalidCurrency',
        params: { rowIndex: error.rowIndex ?? 0 },
      };
    // One message covers all three: the row cannot be read, and the copy names
    // the row rather than which cell of it was unreadable.
    case 'invalid-row':
    case 'invalid-date':
    case 'invalid-cost':
      return {
        key: 'import:errors.invalidRow',
        params: { rowIndex: error.rowIndex ?? 0 },
      };
    default:
      return { key: 'import:errors.parseFailed' };
  }
}

export function readAndParseImportFile(
  text: string,
  resolutions?: LabelResolutions
): ImportFileResult {
  try {
    return { file: parseSplitwiseCsv(text, resolutions) };
  } catch (error) {
    if (error instanceof SplitwiseParseError)
      return { error: parseErrorToKey(error) };
    return { error: { key: 'import:errors.parseFailed' } };
  }
}

export function defaultGroupName(fileName: string): string {
  return fileName
    .replace(/\.csv$/i, '')
    .replace(/^splitwise[_ -]?export[_ -]?/i, '')
    .replace(/_/g, ' ')
    .trim()
    .slice(0, MAX_GROUP_NAME_LENGTH);
}

/**
 * The payload cap is on bytes, and an accented member name costs more of them
 * than the string's length says. Counted by hand rather than through
 * `TextEncoder`/`Blob`, neither of which Hermes guarantees.
 */
export function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      // A surrogate pair is one 4-byte character; skip its low half.
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
}

export function exceedsImportPayloadLimit(
  payload: SplitwiseImportRequestDto
): boolean {
  return utf8ByteLength(JSON.stringify(payload)) > MAX_IMPORT_PAYLOAD_BYTES;
}
